<!--
Generic button with text and/or icon with optional click tracking.

Some styling can be adjusted with CSS variables or classes:
  .fixed-width: 300px button width.
  .condensed:   32px button height.
  --icon-size:  The size of the icon.
-->
<template>
  <Component
    :is="type"
    v-bind="linkParams"
    ref="button"
    :class="['base-button', [variant], {
      'icon-only': !showText,
    }]"
    :disabled="disabled"
    @click.capture="onClick">
    <slot
      v-if="$slots.icon"
      name="icon"/>
    <div
      v-if="showText"
      class="text-nowrap">
      {{ text }}
    </div>
  </Component>
</template>

<script setup>
import { computed, useTemplateRef } from 'vue'
import useLink, { useClickTracker } from '@shared/composables/link.js'

const props = defineProps({
  to: {
    type: [String, Object],
    default: null
  },
  text: {
    type: String,
    default: null
  },
  variant: {
    type: String,
    default: 'primary',
    validator: value => [
      'primary',
      'secondary',
      'tertiary',
      'text-link',
      'text-link-light',
    ].includes(value)
  },
  disabled: {
    type: Boolean,
    default: null
  },
  trackingName: {
    type: String,
    default: null,
    validator: (value, props) => !props.trackingData || value
  },
  trackingData: {
    type: Object,
    default: () => null
  }
})

const emit = defineEmits({
  click: (event) => event instanceof MouseEvent
})

const showText = computed(() => props.text?.length > 0)
const disabled = computed(() => props.disabled)

const link = useLink(() => props.to, { fallbackType: 'button' })
const { type, linkParams } = link

const onTrackedClick = props.trackingName
  ? useClickTracker(
      () => props.trackingName,
      'button',
      () => props.trackingData,
      emit,
      link
    )
  : null
const button = useTemplateRef('button')
function onClick (event) {
  if (disabled.value) {
    event.stopPropagation()
    event.preventDefault()
    return
  }
  button.value?.blur?.() // Button, Anchor
  button.value?.$el?.blur?.() // RouterLink
  if (onTrackedClick) {
    onTrackedClick(event)
  } else {
    emit('click', event)
  }
}

defineExpose({ link })
</script>

<style lang="scss" scoped>
$icon-size: 28px;
$height: 44px;

.base-button {
  @include transition-base-all;

  --icon-size: #{$icon-size};

  padding: 12px 24px;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: fit-content;
  height: $height;
  flex-wrap: nowrap;
  column-gap: $spacing-xxs;
  outline: none;
  border: none;
  border-radius: $sequin-border-radius;

  font-family: $headings-font-family;
  font-weight: $font-weight-semibold;
  font-size: $small-text-font-size;
  line-height: $small-text-line-height;
  text-decoration: none;

  &:has(svg) {
    padding: 8px 24px; // SVG is taller than text.
  }

  :slotted(svg) {
    width: var(--icon-size);
    height: var(--icon-size);
  }

  &.icon-only {
    padding: 7px;
    width: $height;
    // Let variant styles handle colors and borders
  }

  &, &.primary {
    background-color: $primary;
    color: $white;

    @media (hover: hover) {
      &:hover {
        background-color: $plum;
      }
    }
    &:focus:not(:hover), &:active:not([disabled]) {
      box-shadow: 0 0 0 2px $ash;
      -webkit-box-shadow: 0 0 0 2px $ash;
    }
    &[disabled] {
      background-color: $origami;
      color: $ash;
      pointer-events: none;
    }
  }

  &.secondary {
    border: 1px solid $orchid;
    background-color: $origami;
    color: $orchid;

    @media (hover: hover) {
      &:hover {
        border-color: $origami;
        background-color: $plum;
        color: $white;
      }
    }
    &:focus:not(:hover) {
      background-color: $origami;
      color: $plum;
    }
    &[disabled] {
      border: 1px solid $ash;
      background-color: $origami;
      color: $ash;
    }
  }

  &.tertiary {
    border: solid 1px $ash;
    background-color: $white;
    color: $pewter;
    @media (hover: hover) {
      &:hover {
        border: solid 1px $plum;
        background-color: $plum;
        color: $white;
      }
    }
    &:focus:not(:hover), &:active:not([disabled]) {
      border: solid 1px $plum;
      color: $plum;
    }
    &[disabled] {
      color: $ash;
    }
  }

  &.text-link {
    &, &:hover, &:focus:not(:hover) {
      background-color: unset;
      color: $orchid;
      text-decoration: underline;
      font-weight: $font-weight-normal;
    }
    &[disabled] {
      color: $ash;
    }
    &-light {
      background-color: transparent;
      color: $white;
    }
  }

  // Customization classes.
  &.fixed-width {
    width: 300px;
  }
  &.condensed {
    height: 32px;
  }
}
</style>
